import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  SkeletonLoader,
  TableCell,
  TanStackTableRoot,
} from '@toggle/design-system';
import { EarningsItemWithEntity } from '@toggle/toggle';
import React, { useState } from 'react';

import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { TabsType as AOTabsType } from '~/modules/tab-content/types';
import { getHeaderProps } from '~/views/dashboard/calendar-widget/calendar-event-drawer/calendar-event-drawer-header/utils';
import { CalendarEventDrawer } from '~/views/dashboard/calendar-widget/calendar-event-drawer/CalendarEventDrawer';
import { getDrawerContent } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/utils';
import {
  CalendarEvents,
  isEarningsEvent,
} from '~/views/dashboard/calendar-widget/calendar-event-drawer/utils';

import { EmptyState } from '../empty-state/EmptyState';
import { ErrorState } from '../error-state/ErrorState';
import { useEventsColumns } from '../useEventsColumns';
import { formatEventDate, TabsType } from '../utils';
import * as S from './EventsTable.styles';

export interface TableDataSet {
  data: Record<string, CalendarEvents[]>;
  isLoading: boolean;
  isError: boolean;
  handleRefetch: () => void;
}

export interface EventsTableProps {
  activeTab: TabsType;
  activeDate: Date;
  tableDataSet: TableDataSet;
}

export const EventsTable = ({
  activeTab,
  activeDate,
  tableDataSet,
}: EventsTableProps) => {
  const { navigateToPage } = useNavigateToAssetOverview();
  const navigateTerminal = useTerminalNavigate();

  const [drawerData, setDrawerData] = useState<CalendarEvents | undefined>();

  const onEarningsDrawerFullScreenClick = (
    drawerData: EarningsItemWithEntity
  ) =>
    navigateTerminal({
      navigateFn: () =>
        navigateToPage({
          entityTag: drawerData.entity_tag,
          tab: AOTabsType.Earnings,
        }),
      crumb: { label: '', assetTag: drawerData.entity_tag },
    });

  const { data, isError, isLoading, handleRefetch } = tableDataSet;
  const activeDateData = data[formatEventDate(new Date(activeDate))];
  const columns = useEventsColumns({
    activeTab,
  });

  const getAlignment = (index: number) => {
    switch (activeTab) {
      case TabsType.Earnings:
        return index > 2 ? 'right' : 'left';
      case TabsType.Economics:
        return index > 3 ? 'right' : 'left';
      case TabsType.Company:
      default:
        return 'left';
    }
  };

  const table = useReactTable({
    data: activeDateData as CalendarEvents[],
    columns: columns as unknown as ColumnDef<CalendarEvents>[],
    getCoreRowModel: getCoreRowModel(),
  });

  if (isError) {
    return <ErrorState handleReload={handleRefetch} />;
  }

  if (isLoading) {
    const columnLength = table.getHeaderGroups()[0].headers.length;
    return (
      <TanStackTableRoot data-testid="skeleton-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  return (
                    <S.StyledTableHeader
                      key={header.column.id}
                      isHoverEnabled={false}
                      alignment={getAlignment(index)}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </S.StyledTableHeader>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {Array.from({ length: 3 }).map((_, index) => {
            return (
              <tr key={`tr-${index}`}>
                {Array.from({ length: columnLength }).map((_, index) => {
                  return (
                    <TableCell key={index} alignment={getAlignment(index)}>
                      <SkeletonLoader
                        areas={[
                          {
                            styles: {
                              width: '100%',
                              height: '20px',
                              borderRadius: '8px',
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </TanStackTableRoot>
    );
  }

  if (!activeDateData?.length) {
    return <EmptyState activeTab={activeTab} />;
  }

  return (
    <>
      <S.TableWrapper data-testid="events-table">
        <TanStackTableRoot>
          <thead>
            {table.getHeaderGroups().map(headerGroup => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <S.StyledTableHeader
                        key={header.column.id}
                        isHoverEnabled={false}
                        alignment={getAlignment(index)}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </S.StyledTableHeader>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              return (
                <tr key={row.id} onClick={() => setDrawerData(row.original)}>
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <S.StyledTableCell
                        key={cell.id}
                        alignment={getAlignment(index)}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </S.StyledTableCell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </TanStackTableRoot>
      </S.TableWrapper>
      {drawerData && (
        <div
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <CalendarEventDrawer
            closeDrawer={() => {
              setDrawerData(undefined);
            }}
            isOpen={!!drawerData}
            headerContent={getHeaderProps(drawerData)}
            drawerContent={getDrawerContent(drawerData)}
            onFullScreenClick={
              isEarningsEvent(drawerData)
                ? () => onEarningsDrawerFullScreenClick(drawerData)
                : undefined
            }
          />
        </div>
      )}
    </>
  );
};
